'use client';
import React, { useEffect, useState } from 'react';
import {
  GraduationCap,
  BookOpen,
  Award,
  IndianRupee,
  ClipboardList,
  Building2,
  FileText,
  LayoutGrid,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import type { Category } from '@/lib/types';
import { usePublicStore } from './publicStore';

const ICON_MAP: Record<string, React.ElementType> = {
  GraduationCap,
  BookOpen,
  Award,
  IndianRupee,
  ClipboardList,
  Building2,
  FileText,
};

export default function CategoryFilter() {
  const { activeCategory, setActiveCategory, setSearchQuery } = usePublicStore();
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true })
      .then(({ data }) => {
        if (data) setCategories(data as Category[]);
      });
  }, []);

  const handleSelect = (id: string) => {
    setActiveCategory(id);
    setSearchQuery('');
  };

  return (
    <section className="py-4 sm:py-6 px-4 border-b border-border bg-card">
      <div className="max-w-screen-2xl mx-auto">
        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3">
          Browse by Category
        </h2>
        <div className="flex flex-wrap gap-2">
          {/* All */}
          <button
            key="cat-all"
            onClick={() => handleSelect('all')}
            aria-pressed={activeCategory === 'all'}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150 border ${
              activeCategory === 'all' ?'bg-primary text-primary-foreground border-primary shadow-sm' :'bg-background text-foreground border-border hover:border-primary hover:text-primary'
            }`}
          >
            <LayoutGrid size={14} aria-hidden="true" />
            <span>All Forms</span>
          </button>

          {categories.map((cat) => {
            const IconComponent = ICON_MAP[cat.icon] || FileText;
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={`cat-${cat.id}`}
                onClick={() => handleSelect(cat.id)}
                aria-pressed={isActive}
                title={cat.description}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-150 border ${
                  isActive
                    ? 'bg-primary text-primary-foreground border-primary shadow-sm'
                    : 'bg-background text-foreground border-border hover:border-primary hover:text-primary'
                }`}
              >
                <IconComponent size={14} aria-hidden="true" />
                <span>{cat.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}